import React, { useEffect, useState } from 'react';
import { Club } from '../../types/types';

interface ProgressBarsProps {
  items: Club[];
  activeItems: Club[];
  multipleSelect?: boolean;
  label: string;
  activeGender?: string;
  onItemClick: (club: Club) => void;
}

import {
  ChevronUpDownIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import NotFind from './notfind';

const SelectBoxClub: React.FC<ProgressBarsProps> = ({
  items,
  activeItems,
  onItemClick,
  label,
  multipleSelect = false,
}) => {
  const [modalState, setModalState] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const modalRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setModalState(false);
      }
    };

    if (modalState) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modalState]);

  const filteredClubs = items.filter(
    (club) =>
      club.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      club.club_number.toString().includes(searchQuery),
  );

  return (
    <div
      className={`border relative min-w-[180px] bg-white cursor-pointer hover:border-gray-400 hover:shadow-md rounded-md shadow-sm ${modalState && 'border-gray-500 hover:border-gray-500'} ${!multipleSelect && activeItems.length === 1 && 'border-green-600 hover:border-green-700'}`}
    >
      <div
        onClick={() => setModalState(true)}
        className="flex p-1.5 justify-between items-center gap-2"
      >
        <div className="w-5 text-md hidden md:inline-block">⛳</div>
        <div
          className={`text-xs font-medium ${!multipleSelect && activeItems.length === 0 && 'text-gray-600'}`}
        >
          {!multipleSelect && activeItems.length
            ? `${activeItems[0].name}`
            : label}
        </div>
        {multipleSelect && (
          <div className="text-xs text-gray-400 hidden sm:inline-block">
            ({activeItems.length}/{items.length})
          </div>
        )}
        <ChevronUpDownIcon className="w-4" />
      </div>

      {modalState && (
        <div
          ref={modalRef}
          className="animate__animated animate__fadeIn animate__faster absolute overflow-hidden z-10 w-[230px] bg-white border border-gray-400 top-11 right-0 rounded-md shadow-md"
        >
          <div className="border-b shadow-md border-gray-400 gap-2 p-2 flex rounded-t-md">
            <div className="w-5 text-xl hidden md:inline-block grayscale">
              ⛳
            </div>
            <input
              className="p-1 flex-1 w-[100px] bg-transparent border-transparent focus:border-transparent focus:ring-0 focus:outline-none"
              placeholder="suchen..."
              name="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div
              onClick={() => setModalState(false)}
              className="w-7 h-7 bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center rounded-md"
            >
              <XMarkIcon className="w-5" />
            </div>
          </div>

          <div className="overflow-scroll h-[300px]">
            {filteredClubs.length === 0 && (
              <div className="text-center flex items-center justify-center h-[280px]">
                <NotFind
                  displayIcon
                  title="Suche erfolglos"
                  description="Kein Club mit diesem Schlüsselwort gefunden"
                />
              </div>
            )}
            {filteredClubs.map((club, index) => (
              <div
                key={index}
                onClick={() => {
                  onItemClick(club);
                  if (!multipleSelect) setModalState(false);
                }}
                className={`border-b px-2 hover:bg-neutral-100 group justify-between flex gap-2 items-center p-1 ${activeItems.some((activeItem) => activeItem.id === club.id) && 'bg-green-50 border-l-green-500 border-l-2'}`}
              >
                <div
                  title={`${club.loginTime && `Zuletzt eingeloggt: ${club.loginTime}`}`}
                  className="flex items-center gap-3"
                >
                  <div
                    className={`bg-green-50 px-1 text-green-800 font-bold text-xs ease-in-out transition-all flex items-center justify-center rounded-md`}
                  >
                    {club.club_number}
                  </div>
                  <div className={`text-xs ${club.loginTime && 'font-bold'}`}>
                    {club.name}
                  </div>
                </div>
                {activeItems.some(
                  (activeItem) => activeItem.id === club.id,
                ) && <CheckIcon className="w-4" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectBoxClub;
